import React, { useEffect } from 'react';
import { Check, X } from 'lucide-react';
import { cn } from '@/utils/cn';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastProps {
  message: string;
  type?: ToastType;
  duration?: number;
  onClose: () => void;
  visible: boolean;
}

const Toast: React.FC<ToastProps> = ({
  message,
  type = 'info',
  duration = 3000,
  onClose,
  visible,
}) => {
  useEffect(() => {
    if (visible) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [visible, duration, onClose]);

  const icons = {
    success: <Check className="w-4 h-4 text-[#16A34A]" />,
    error: <X className="w-4 h-4 text-[#DC2626]" />,
    info: (
      <svg className="w-4 h-4 text-[#3B82F6]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    warning: (
      <svg className="w-4 h-4 text-[#B77700]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
  };

  const borderColors = {
    success: 'border-l-[#16A34A]',
    error: 'border-l-[#DC2626]',
    info: 'border-l-[#3B82F6]',
    warning: 'border-l-[#B77700]',
  };

  return (
    <div
      className={cn(
        'fixed top-4 right-4 z-50 max-w-sm p-3.5 rounded-xl shadow-lg border border-[#D2D2D7] bg-white border-l-2',
        borderColors[type],
        'transform transition-all duration-300 ease-in-out',
        visible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0',
      )}
    >
      <div className="flex items-start gap-2.5">
        <div className="flex-shrink-0 mt-0.5">{icons[type]}</div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-medium text-[#111111] leading-snug">{message}</p>
        </div>
        <button
          onClick={onClose}
          className="flex-shrink-0 -mt-0.5 -mr-1 p-1 rounded-md text-[#8A8A90] hover:text-[#111111] hover:bg-[#F5F5F7] transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default Toast;